import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail
        ? formatApiError(err.response.data.detail)
        : (err?.message || "Network error — could not reach the server."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface" data-testid="reset-password-page">
      <div className="p-8 md:p-16 pb-0">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-ember" />
          <img src="/brand/symbol-color.svg" alt="" className="h-7 w-auto" />
          <span className="font-display font-medium text-2xl tracking-tight">wandr</span>
        </div>
      </div>

      <div className="flex-1 flex items-center">
        <div className="max-w-md w-full mx-auto px-8 py-12">
          {!token ? (
            <div data-testid="reset-invalid-link">
              <p className="label-tag mb-3">Reset password</p>
              <h1 className="heading-h1 text-4xl mb-2">Invalid link.</h1>
              <div className="divider-strong my-6" />
              <p className="text-sand-600 text-base leading-relaxed mb-8">
                This reset link is missing its token. Please request a new one from the sign-in page.
              </p>
              <Link to="/auth" className="btn-swiss btn-primary inline-block" data-testid="reset-back-to-signin">
                Back to sign in →
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <p className="label-tag mb-3">All set</p>
              <h1 className="heading-h1 text-4xl mb-2">Password updated.</h1>
              <div className="divider-strong my-6" />
              <p className="text-sand-600 text-base leading-relaxed mb-8">
                Your password has been changed. Sign in with your new password.
              </p>
              <Link to="/auth" className="btn-swiss btn-primary inline-block" data-testid="reset-success-signin">
                Sign in →
              </Link>
            </div>
          ) : (
            <div>
              <p className="label-tag mb-3">Reset password</p>
              <h1 className="heading-h1 text-4xl sm:text-5xl mb-2">New password.</h1>
              <div className="divider-strong my-6" />
              <p className="text-sand-600 text-base leading-relaxed mb-8">
                Choose a new password for your Wandr account.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <label className="label-tag block mb-2">New password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="swiss-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <label className="label-tag block mb-2">Confirm password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="swiss-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-input"
                  />
                </div>

                {error && (
                  <div className="border border-error bg-red-50 text-error px-4 py-3 text-sm" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="btn-swiss btn-primary w-full disabled:opacity-50"
                  data-testid="reset-submit"
                >
                  {busy ? "..." : "Update password"}
                </button>
              </form>

              <Link
                to="/auth"
                className="mt-6 inline-block text-sm text-sand-600 hover:text-ember transition-colors"
                data-testid="reset-back-link"
              >
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="p-8 md:p-16 pt-0 label-tag">© 2026 Wandr · MVP</div>
    </div>
  );
}
